import React, { FunctionComponent, useMemo, useState } from "react";
import { Grid, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { ILabel, useGetLabels } from "../../../../hooks/labels";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifyLabelDialog } from "../ModifyLabelDialog";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { LabelCard } from "../LabelCard";
import { LabelDialog } from "../LabelDialog";
import { useContextMenu } from "../../../../hooks/contextMenu";
import styles from "./styles.module.scss";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { Icon, IconType } from "../../../../icons";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";

export const LabelsDesktop: FunctionComponent<ILabelsDesktopProps> = ({
  handleClose,
  isPublic,
  columnWidth,
  preselectedId,
  onLabelSelected,
  onClearValue
}) => {
  const [loading, setLoading] = useState(true);
  const [labels, resetLabels, setLabels, noMoreLabels] = useGetLabels(isPublic, setLoading);
  const [imageData, setImageData] = useState<string>();
  const {
    state: labelDialogState,
    key: labelDialogKey,
    openEdit: openLabelDialog,
    close: closeLabelDialog
  } = useResettableDialog<ILabel>();
  const {
    state: modifyLabelDialogState,
    key: modifyLabelDialogKey,
    openAdd,
    openEdit,
    close: closeModifyLabelDialog
  } = useResettableDialog<ILabel>();
  const [searchText, setSearchText] = useState("");
  const [contextLabel, setContextLabel] = useState<ILabel>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const filteredLabels = useMemo(() => {
    if (searchText == "") {
      return labels;
    }
    return labels?.filter(label => {
      return label.name.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [labels, searchText]);
  const onModifiedLabel = (newLabel: ILabel) => {
    const newLabels = labels.map(label => (label.uuid == newLabel.uuid ? newLabel : label));
    newLabels.sort((a, b) => a.name.localeCompare(b.name, undefined, { ignorePunctuation: true }));
    setLabels(newLabels);
    toast.success(strings.generic_update_success);
  };
  const onDeletedLabel = (deletedLabel: ILabel) => {
    const newLabels = labels.filter(label => label.uuid != deletedLabel.uuid);
    setLabels(newLabels);
    toast.success(strings.generic_update_success);
  };
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={isPublic}
      items={filteredLabels}
      emptyIconType={attributeIconMap[AttributeType.LABELS]}
      emptyString={isPublic ? strings.empty_labels_public : strings.empty_labels}
      addItemString={strings.add_attribute}
      title={strings.attribute_labels_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={() => {
        setImageData(undefined);
        openAdd();
      }}
      refreshAction={resetLabels}
      onClearValue={onClearValue}
    >
      {filteredLabels?.map(label => (
        <Grid item key={label.uuid} xs={columnWidth}>
          <LabelCard
            label={label}
            selected={preselectedId == label.uuid}
            onClick={(_event, imageData) => {
              setImageData(imageData);
              if (onLabelSelected) {
                onLabelSelected(label);
              } else {
                openEdit(label);
              }
            }}
            onContextMenu={(event, imageData) => {
              setImageData(imageData);
              setContextLabel(label);
              handleContextMenu(event);
            }}
          />
        </Grid>
      ))}
      {(loading || !noMoreLabels) &&
        Array.from({ length: 40 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 60 }} className={styles.skeleton} />
          </Grid>
        ))}
      <ModifyLabelDialog
        key={modifyLabelDialogKey}
        open={modifyLabelDialogState.mode !== "closed"}
        onClose={closeModifyLabelDialog}
        label={modifyLabelDialogState.item}
        originalImageData={imageData}
        onNewItem={newLabel => {
          const newLabels = [...labels, newLabel];
          newLabels.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setLabels(newLabels);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={onModifiedLabel}
        onDeletedItem={onDeletedLabel}
      />
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openLabelDialog(contextLabel);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.FrameInspect} />
          </ListItemIcon>
          {strings.label_view}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextLabel);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.label_edit}
        </MenuItem>
      </Menu>
      {labelDialogState.item && (
        <LabelDialog
          key={labelDialogKey}
          label={labelDialogState.item}
          isOpen={labelDialogState.mode !== "closed"}
          onClose={closeLabelDialog}
          canEdit={true}
          onModifiedItem={onModifiedLabel}
          onDeletedItem={deletedLabel => {
            closeLabelDialog();
            onDeletedLabel(deletedLabel);
          }}
        />
      )}
    </AttributeDesktop>
  );
};

export interface ILabelsDesktopProps {
  handleClose?: () => void;
  isPublic: boolean;
  columnWidth: number;
  preselectedId?: string;
  onLabelSelected?: (label: ILabel) => void;
  onClearValue?: () => void;
}
